import {
  ADMINISTRATIVE_GEOGRAPHY_TAXONOMY_ID,
  MSA_GEOGRAPHY_TAXONOMY_ID,
} from '../../../../shared/user-preferences/utils/geography-preference';
import type { CustomGeographyTaxonomySelection } from '../definitions/geographyTaxonomy';
import { getFilterDefinition } from '../engine/filterRegistry';
import {
  type Filter,
  FilterId,
  type SingleValueFilter,
} from '../types/filters';
import type { TaxonomyFilterRemoval } from './taxonomyFilterRemoval';

const KNOWN_GEOGRAPHY_TAXONOMY_IDS: ReadonlySet<number> = new Set([
  MSA_GEOGRAPHY_TAXONOMY_ID,
  ADMINISTRATIVE_GEOGRAPHY_TAXONOMY_ID,
]);

/**
 * A geography taxonomy id the app cannot resolve is worse than none: the tree
 * fetch returns no levels, the selection-list fallback hands back METRO keys
 * regardless, and the filter engine then re-keys selections by id depth against
 * a level set that does not belong to the id. Reset to the definition default
 * ("no opinion") so the viewer's own preference resolves instead.
 */
const isKnownTaxonomySelection = (
  value: unknown
): value is CustomGeographyTaxonomySelection => {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as { taxonomyId?: unknown };
  return (
    typeof candidate.taxonomyId === 'number' &&
    KNOWN_GEOGRAPHY_TAXONOMY_IDS.has(candidate.taxonomyId)
  );
};

/**
 * Validates the geography taxonomy carried by a deeplink or saved view.
 *
 * Unlike role/skill/activity there is no version to check: every geography tree
 * fetch resolves ONE shared latest version (see `useGeographyTaxonomyTree`), so
 * the only thing a link can get wrong is the taxonomy id itself.
 *
 * A valid id — metro included — is marked `isUserSubmitted`, which is what
 * `SyncInitialPreferencesToFilters` keys its per-filter backoff on: the link
 * wins the view and the viewer's stored preference is left alone.
 */
export const validateGeographyFilters = ({
  filters,
  onRemoveFilter,
}: {
  filters: [FilterId, Filter][];
  onRemoveFilter?: (filterId: FilterId, removal: TaxonomyFilterRemoval) => void;
}): [FilterId, Filter][] => {
  const geographyTaxonomyEntry = filters.find(
    ([id]) => id === FilterId.GEOGRAPHY_TAXONOMY
  );
  if (!geographyTaxonomyEntry) return filters;

  const filter = geographyTaxonomyEntry[1] as SingleValueFilter<unknown>;
  const value = filter.value;

  // No taxonomy expressed — not a removal, so it must not raise the toast.
  const isNeutral =
    typeof value === 'object' &&
    value !== null &&
    (value as { taxonomyId?: unknown }).taxonomyId === 0;
  if (isNeutral) return filters;

  if (isKnownTaxonomySelection(value)) {
    return filters.map(([id, entry]) =>
      id === FilterId.GEOGRAPHY_TAXONOMY
        ? [id, { ...entry, isUserSubmitted: true }]
        : [id, entry]
    );
  }

  const definition = getFilterDefinition(FilterId.GEOGRAPHY_TAXONOMY);
  onRemoveFilter?.(FilterId.GEOGRAPHY_TAXONOMY, {
    reason: 'unknown_taxonomy',
  });

  return filters.map(([id, entry]) =>
    id === FilterId.GEOGRAPHY_TAXONOMY
      ? [
          id,
          {
            ...entry,
            value: definition?.value ?? { taxonomyId: 0 },
            isUserSubmitted: false,
          } as Filter,
        ]
      : [id, entry]
  );
};
